/**
 * <UnderlineTabs> — des onglets soulignés, pour changer de vue dans une même page (molecule).
 *
 * Le soulignement de `<BottomTab>`, en haut d'un contenu : l'onglet ouvert s'écrit à la couleur de
 * l'univers et porte le trait, les autres restent gris. Rien d'un bouton : ni fond, ni relief. C'est
 * ce qui les distingue de `<ChoiceChip>`, la pilule d'une réponse qu'on choisit.
 *
 * Un onglet peut dire un compte, en gris à côté de son libellé (`count`), ou qu'il y a du neuf, par un
 * point (`dot`, `<NotificationDot>`). La rangée défile quand elle déborde, et amène à l'écran l'onglet
 * ouvert (la pastille d'un élève ouvre Messages, le dernier). Contrôlé.
 */
import React, { useState } from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { HorizontalList } from "../../atoms/HorizontalList"
import { NotificationDot } from "../../atoms/NotificationDot"
import { Text } from "../../atoms/typography/Text"

const DOT_SIZE = 8

export type UnderlineTab<Key extends string> = {
    key: Key
    label: string
    /** Le compte, en gris après le libellé. Absent : rien. */
    count?: number
    /** Il y a du neuf derrière cet onglet. Défaut `false`. */
    dot?: boolean
}

export type UnderlineTabsProps<Key extends string> = {
    tabs: UnderlineTab<Key>[]
    value: Key
    onChange: (key: Key) => void
    testID?: string
}

export function UnderlineTabs<Key extends string>(props: UnderlineTabsProps<Key>) {
    const { tabs, value, onChange, testID } = props
    const [positions, setPositions] = useState<Partial<Record<Key, number>>>({})
    const selectedX = positions[value]
    return (
        <View style={styles.root} accessibilityRole="tablist" testID={testID}>
            <HorizontalList gap="5" scrollToX={selectedX}>
                {tabs.map((tab) => {
                    const selected = tab.key === value
                    return (
                        <Pressable
                            key={tab.key}
                            onPress={() => onChange(tab.key)}
                            onLayout={(event) => {
                                const { x } = event.nativeEvent.layout
                                setPositions((known) => (known[tab.key] === x ? known : { ...known, [tab.key]: x }))
                            }}
                            accessibilityRole="tab"
                            accessibilityState={{ selected }}
                            aria-selected={selected}
                            style={styles.tab(selected)}
                        >
                            <Text size="text-md" weight="bold" color={selected ? "role-strong" : "text-muted"}>
                                {tab.label}
                            </Text>
                            {tab.count != null && (
                                <Text size="text-sm" color="text-muted">
                                    {tab.count}
                                </Text>
                            )}
                            {tab.dot === true && <NotificationDot size={DOT_SIZE} ringColor="surface" />}
                        </Pressable>
                    )
                })}
            </HorizontalList>
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    root: { flexGrow: 0, flexShrink: 0, borderBottomWidth: 1, borderBottomColor: theme.colors["border-subtle"] },
    tab: (selected: boolean) => ({
        minHeight: 44,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["1"],
        borderBottomWidth: 2,
        borderBottomColor: selected ? theme.colors.role : "transparent",
    }),
}))
